import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { MODULES } from '@/types/course';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/features/auth';

// Base storage key - will be combined with user ID for logged-in users
const STORAGE_KEY_BASE = 'kbt-course-progress';
const getStorageKey = (userId?: string) => userId ? `${STORAGE_KEY_BASE}-${userId}` : STORAGE_KEY_BASE;

interface CourseProgressContextType {
  completedChapters: Set<string>;
  isLoading: boolean;
  markChapterComplete: (chapterId: string) => void;
  isChapterCompleted: (chapterId: string) => boolean;
  getTotalProgress: () => number;
  getModuleProgress: (moduleId: string) => number;
  getChaptersWithStatus: () => any[];
}

const CourseProgressContext = createContext<CourseProgressContextType | undefined>(undefined);

export function CourseProgressProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [completedChapters, setCompletedChapters] = useState<Set<string>>(new Set());
  const [isLoading, setIsLoading] = useState(true);

  // Helper function to extract session number from chapterId
  const extractSessionNumber = (chapterId: string): number => {
    // Format: "kapitel-X-Y" where X is module, Y is session within module
    const parts = chapterId.split('-');
    if (parts.length === 3) {
      const moduleNum = parseInt(parts[1]);
      const sessionNum = parseInt(parts[2]);
      return (moduleNum - 1) * 12 + sessionNum;
    }
    return 0;
  };

  // Load progress from database first, fallback to user-specific localStorage
  useEffect(() => {
    const loadProgress = async () => {
      // Reset state when user changes
      setCompletedChapters(new Set());

      if (!user) {
        // Not logged in, load from anonymous localStorage
        const saved = localStorage.getItem(getStorageKey());
        if (saved) {
          try {
            const data = JSON.parse(saved);
            setCompletedChapters(new Set(data.completedChapters || []));
          } catch (error) {
            console.error('Failed to parse saved progress:', error);
          }
        }
        setIsLoading(false);
        return;
      }

      try {
        // Load from database for logged-in user
        const { data, error } = await supabase
          .from('session_completions')
          .select('session_id')
          .eq('client_id', user.id)
          .eq('status', 'completed');

        if (error) throw error;

        if (data && data.length > 0) {
          // Convert session_ids to chapter_ids (kapitel-X-Y format)
          const chapterIds = data.map(item => {
            const moduleNum = Math.ceil(item.session_id / 12);
            const sessionInModule = item.session_id - (moduleNum - 1) * 12;
            return `kapitel-${moduleNum}-${sessionInModule}`;
          });
          setCompletedChapters(new Set(chapterIds));
        } else {
          // No data in database, try user-specific localStorage
          const saved = localStorage.getItem(getStorageKey(user.id));
          if (saved) {
            try {
              const data = JSON.parse(saved);
              setCompletedChapters(new Set(data.completedChapters || []));
            } catch (error) {
              console.error('Failed to parse saved progress:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error loading progress from database:', error);
        // Fallback to user-specific localStorage
        const saved = localStorage.getItem(getStorageKey(user.id));
        if (saved) {
          try {
            const data = JSON.parse(saved);
            setCompletedChapters(new Set(data.completedChapters || []));
          } catch (error) {
            console.error('Failed to parse saved progress:', error);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [user?.id]);

  // Save progress to user-specific localStorage whenever it changes
  useEffect(() => {
    if (!isLoading && completedChapters.size > 0) {
      const progressData = {
        completedChapters: Array.from(completedChapters)
      };
      // Use user-specific key if logged in, anonymous key otherwise
      localStorage.setItem(getStorageKey(user?.id), JSON.stringify(progressData));
    }
  }, [completedChapters, isLoading, user?.id]);

  const markChapterComplete = async (chapterId: string) => {
    // Update local state immediately
    setCompletedChapters(prev => new Set([...prev, chapterId]));

    // Persist to database if user is logged in
    if (user) {
      try {
        const sessionNumber = extractSessionNumber(chapterId);
        const moduleNumber = Math.ceil(sessionNumber / 12);

        // Get chapter title from MODULES
        const module = MODULES.find(m => m.id === `modul-${moduleNumber}`);
        const chapter = module?.chapters.find(ch => ch.id === chapterId);
        const sessionTitle = chapter?.title || `Session ${sessionNumber}`;

        // Insert or update session completion
        const { error: sessionError } = await supabase
          .from('session_completions')
          .upsert({
            client_id: user.id,
            session_id: sessionNumber,
            module_id: moduleNumber,
            session_title: sessionTitle,
            status: 'completed',
            completed_at: new Date().toISOString()
          }, {
            onConflict: 'client_id,session_id'
          });

        if (sessionError) throw sessionError;

        // Update overall program progress
        const completedCount = completedChapters.size + 1;
        const now = new Date().toISOString();

        // Fetch existing record to preserve program_started_at
        const { data: existingProgress } = await supabase
          .from('client_program_progress')
          .select('program_started_at')
          .eq('client_id', user.id)
          .maybeSingle();

        const { error: progressError } = await supabase
          .from('client_program_progress')
          .upsert({
            client_id: user.id,
            total_sessions_completed: completedCount,
            completion_percentage: (completedCount / 48) * 100,
            program_started_at: existingProgress?.program_started_at || now,
            last_activity_at: now,
            program_status: completedCount >= 48 ? 'completed' : 'in_progress',
            [`module_${moduleNumber}_completed`]: module?.chapters.filter(ch =>
              completedChapters.has(ch.id) || ch.id === chapterId
            ).length || 0
          }, {
            onConflict: 'client_id'
          });

        if (progressError) throw progressError;
      } catch (error) {
        console.error('Error saving progress to database:', error);
        // Progress is still saved locally, so this isn't critical
      }
    }
  };

  const isChapterCompleted = (chapterId: string) => {
    return completedChapters.has(chapterId);
  };

  const getTotalProgress = () => {
    const totalChapters = MODULES.reduce((acc, module) => acc + module.chapters.length, 0);
    const completedCount = completedChapters.size;
    return Math.round((completedCount / totalChapters) * 100);
  };

  const getModuleProgress = (moduleId: string) => {
    const module = MODULES.find(m => m.id === moduleId);
    if (!module) return 0;

    const moduleChapterIds = module.chapters.map(ch => ch.id);
    const completedInModule = moduleChapterIds.filter(id => completedChapters.has(id)).length;
    return Math.round((completedInModule / moduleChapterIds.length) * 100);
  };

  const getChaptersWithStatus = () => {
    return MODULES.map(module => ({
      ...module,
      chapters: module.chapters.map(chapter => ({
        ...chapter,
        completed: completedChapters.has(chapter.id)
      }))
    }));
  };

  return (
    <CourseProgressContext.Provider
      value={{
        completedChapters,
        isLoading,
        markChapterComplete,
        isChapterCompleted,
        getTotalProgress,
        getModuleProgress,
        getChaptersWithStatus
      }}
    >
      {children}
    </CourseProgressContext.Provider>
  );
}

export function useCourseProgress() {
  const context = useContext(CourseProgressContext);
  if (context === undefined) {
    throw new Error('useCourseProgress must be used within a CourseProgressProvider');
  }
  return context;
}